import type { PreviewContract } from "../contract";
import { PREVIEW_HIDE_DELAY_MS, PREVIEW_SHOW_DELAY_MS } from "../defaults";
import type { PreviewFetcher } from "./fetcher";
import { createPopoverRenderer } from "./popover";
import { createPositionedPopover, type PositionedPopover } from "./positioning";
import { type EmbedConfig, type ResolvedPreviewTarget } from "./types";

export interface TriggerHandle {
  destroy(): void;
}

export interface CreateTriggerOptions {
  element: HTMLElement;
  target: ResolvedPreviewTarget;
  config: EmbedConfig;
  fetcher: PreviewFetcher;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function createTrigger(options: CreateTriggerOptions): TriggerHandle {
  const popover = createPopoverRenderer(options.config.theme);

  const openDelay = prefersReducedMotion()
    ? 0
    : (options.config.showDelay ?? PREVIEW_SHOW_DELAY_MS);
  const hideDelay = prefersReducedMotion()
    ? 0
    : (options.config.hideDelay ?? PREVIEW_HIDE_DELAY_MS);

  let destroyed = false;
  let hovered = false;
  let focused = false;
  let touchActive = false;
  let pendingFetch = false;
  let resolvedData: PreviewContract | undefined;
  let showTimer: number | undefined;
  let hideTimer: number | undefined;
  let abortController: AbortController | undefined;
  let positionedPopover: PositionedPopover | undefined;

  const isVisible = (): boolean => popover.element.style.display !== "none";
  const isActive = (): boolean => hovered || focused || touchActive;

  const clearShowTimer = (): void => {
    if (showTimer !== undefined) {
      window.clearTimeout(showTimer);
      showTimer = undefined;
    }
  };

  const clearHideTimer = (): void => {
    if (hideTimer !== undefined) {
      window.clearTimeout(hideTimer);
      hideTimer = undefined;
    }
  };

  const clearAbortController = (): void => {
    abortController?.abort();
    abortController = undefined;
    pendingFetch = false;
  };

  const ensurePositioning = (): void => {
    if (!positionedPopover) {
      positionedPopover = createPositionedPopover(
        options.element,
        popover.element,
      );
      return;
    }

    void positionedPopover.update();
  };

  const hideImmediately = (): void => {
    clearShowTimer();
    clearHideTimer();
    popover.hide();
    positionedPopover?.destroy();
    positionedPopover = undefined;

    if (!isActive()) {
      clearAbortController();
    }
  };

  const showPopover = (): void => {
    if (destroyed || !isActive()) {
      return;
    }

    showTimer = undefined;

    if (resolvedData) {
      popover.showData(resolvedData);
      ensurePositioning();
      return;
    }

    if (pendingFetch) {
      popover.showLoading();
      ensurePositioning();
    }
  };

  const scheduleShow = (): void => {
    if (isVisible()) {
      return;
    }

    clearShowTimer();

    if (openDelay === 0) {
      showPopover();
      return;
    }

    showTimer = window.setTimeout(showPopover, openDelay);
  };

  const scheduleHide = (): void => {
    clearShowTimer();

    if (!isVisible()) {
      if (!isActive()) {
        clearAbortController();
      }
      return;
    }

    clearHideTimer();

    if (hideDelay === 0) {
      hideImmediately();
      return;
    }

    hideTimer = window.setTimeout(hideImmediately, hideDelay);
  };

  const fetchPreview = (): void => {
    if (resolvedData || pendingFetch) {
      return;
    }

    abortController = new AbortController();
    pendingFetch = true;

    void options.fetcher
      .fetchPreview(options.target, abortController.signal)
      .then((data) => {
        if (destroyed || abortController?.signal.aborted) {
          return;
        }

        pendingFetch = false;
        abortController = undefined;
        resolvedData = data;

        if (!data) {
          hideImmediately();
          return;
        }

        if (!isActive()) {
          return;
        }

        if (isVisible()) {
          popover.showData(data);
          ensurePositioning();
          return;
        }

        if (showTimer === undefined && openDelay === 0) {
          popover.showData(data);
          ensurePositioning();
        }
      })
      .catch((error: unknown) => {
        if (destroyed || abortController?.signal.aborted) {
          return;
        }

        pendingFetch = false;
        abortController = undefined;
        hideImmediately();
        console.error("[LibrariumPreview] Failed to load preview", error);
      });
  };

  const activate = (interaction: "hover" | "focus" | "touch"): void => {
    if (interaction === "touch") {
      touchActive = true;
    }

    clearHideTimer();
    fetchPreview();
    scheduleShow();
  };

  const handleMouseEnter = (): void => {
    hovered = true;
    activate("hover");
  };

  const handleMouseLeave = (): void => {
    hovered = false;
    if (!isActive()) {
      scheduleHide();
    }
  };

  const handleFocusIn = (): void => {
    focused = true;
    activate("focus");
  };

  const handleFocusOut = (event: FocusEvent): void => {
    const nextTarget = event.relatedTarget;

    if (nextTarget instanceof Node && options.element.contains(nextTarget)) {
      return;
    }

    focused = false;
    if (!isActive()) {
      scheduleHide();
    }
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") {
      return;
    }

    hovered = false;
    focused = false;
    touchActive = false;
    hideImmediately();
  };

  const handlePointerDown = (event: PointerEvent): void => {
    if (event.pointerType === "mouse") {
      return;
    }

    if (!isVisible()) {
      event.preventDefault();
      activate("touch");
      return;
    }

    touchActive = true;
  };

  const handleDocumentPointerDown = (event: PointerEvent): void => {
    if (!isVisible()) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    if (options.element.contains(target) || popover.contains(target)) {
      return;
    }

    hovered = false;
    focused = false;
    touchActive = false;
    hideImmediately();
  };

  options.element.addEventListener("mouseenter", handleMouseEnter);
  options.element.addEventListener("mouseleave", handleMouseLeave);
  options.element.addEventListener("focusin", handleFocusIn);
  options.element.addEventListener("focusout", handleFocusOut);
  options.element.addEventListener("keydown", handleKeyDown);
  options.element.addEventListener("pointerdown", handlePointerDown);
  document.addEventListener("pointerdown", handleDocumentPointerDown, true);

  return {
    destroy() {
      if (destroyed) {
        return;
      }

      destroyed = true;
      options.element.removeEventListener("mouseenter", handleMouseEnter);
      options.element.removeEventListener("mouseleave", handleMouseLeave);
      options.element.removeEventListener("focusin", handleFocusIn);
      options.element.removeEventListener("focusout", handleFocusOut);
      options.element.removeEventListener("keydown", handleKeyDown);
      options.element.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener(
        "pointerdown",
        handleDocumentPointerDown,
        true,
      );
      clearShowTimer();
      clearHideTimer();
      clearAbortController();
      positionedPopover?.destroy();
      popover.destroy();
    },
  };
}
